/**
 * ScraperHelp — reusable "how to use" panel that pairs with the scraper
 * pages (Instagram / LinkedIn / YouTube). Pass a `steps` array and it
 * renders a clean numbered walkthrough with code blocks + tip callouts.
 */
import { BookOpen, ChevronRight, Lightbulb, AlertCircle, Link2 } from "lucide-react";

export default function ScraperHelp({ title, intro, steps = [], tips = [], troubleshooting = [], links = [] }) {
  return (
    <div className="al-card p-6 max-w-4xl" data-testid="scraper-help">
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-5 h-5 text-[#3B82F6]" />
        <h2 className="text-[17px] font-bold text-[var(--al-text)]">{title}</h2>
      </div>
      {intro && <p className="text-[13px] text-[var(--al-text-muted)] leading-relaxed mb-5">{intro}</p>}

      <ol className="space-y-4" data-testid="help-steps">
        {steps.map((s, i) => (
          <li key={`step-${s.title || i}`} className="flex gap-3">
            <span className="shrink-0 w-7 h-7 rounded-full bg-[#3B82F6] text-white text-[12px] font-bold flex items-center justify-center">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0 pt-0.5">
              <h3 className="text-[13.5px] font-bold text-[var(--al-text)] mb-1">{s.title}</h3>
              {s.body && <p className="text-[12.5px] text-[var(--al-text)] leading-relaxed whitespace-pre-wrap">{s.body}</p>}
              {s.code && (
                <pre className="mt-2 bg-[#0f172a] text-[#a3e635] text-[11px] p-2.5 rounded overflow-x-auto font-mono">
                  {s.code}
                </pre>
              )}
              {s.link && (
                <a href={s.link.href} target="_blank" rel="noopener noreferrer"
                   className="mt-2 inline-flex items-center gap-1 text-[12px] text-[#3B82F6] hover:underline font-semibold">
                  <Link2 className="w-3 h-3" /> {s.link.label}
                  <ChevronRight className="w-3 h-3" />
                </a>
              )}
            </div>
          </li>
        ))}
      </ol>

      {tips.length > 0 && (
        <div className="mt-6 rounded-lg bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-center gap-1.5 text-amber-700 text-[12px] font-bold uppercase tracking-wide mb-2">
            <Lightbulb className="w-3.5 h-3.5" /> Tips
          </div>
          <ul className="space-y-1.5 text-[12.5px] text-amber-800 leading-snug">
            {tips.map((t, i) => (
              <li key={`tip-${t.slice(0, 20) || i}`} className="flex gap-2">
                <span className="text-amber-500">•</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {troubleshooting.length > 0 && (
        <div className="mt-4 rounded-lg bg-rose-50 border border-rose-200 p-4">
          <div className="flex items-center gap-1.5 text-rose-700 text-[12px] font-bold uppercase tracking-wide mb-2">
            <AlertCircle className="w-3.5 h-3.5" /> Troubleshooting
          </div>
          <ul className="space-y-2 text-[12.5px] text-rose-800 leading-snug">
            {troubleshooting.map((t, i) => (
              <li key={`trb-${t.problem?.slice(0, 20) || i}`}>
                <b>{t.problem}</b> — {t.solution}
              </li>
            ))}
          </ul>
        </div>
      )}

      {links.length > 0 && (
        <div className="mt-4 pt-4 border-t border-[var(--al-border)] flex flex-wrap gap-2">
          {links.map((l) => (
            <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#3B82F6] hover:underline">
              <Link2 className="w-3 h-3" /> {l.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
